import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/Badge';
import { ClaimDetailsModal } from '@/components/ClaimDetailsModal';
import { ClaimsList } from '@/components/ClaimsList';
import { Header } from '@/components/Header';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageContainer } from '@/components/PageContainer';

import { useAdminClaims } from '@/hooks/useAdminClaims';
import { useAdminUser } from '@/hooks/useAdminUser';
import { useAdminUserEdit } from '@/hooks/useAdminUserEdit';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import type { IAdminClaim } from '@/services/admin';

import type { IClaim } from '@/types/claim';
import type { IAdminUser } from '@/types/user';

import { ADMIN_DEEL_CONTRACT_ID, isFeatureEnabled } from '@/utils/feature-flags';
import { formatDate } from '@/utils/formatting';
import { capitalizeFirstLetter, getInitials } from '@/utils/stringUtils';

type AdminUserDetailsTab = 'details' | 'claims' | 'balance';

function AdminUserDetails() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const [activeTab, setActiveTab] = useState<AdminUserDetailsTab>('details');
  const [selectedClaim, setSelectedClaim] = useState<IClaim | null>(null);
  const { data: featureFlags } = useFeatureFlags();
  const deelContractIdEnabled = isFeatureEnabled(featureFlags, ADMIN_DEEL_CONTRACT_ID);

  const state = location.state as { user?: IAdminUser } | null;
  const selectedUser = state?.user;

  const { data: fetchedUser, loading: userLoading, refetch } = useAdminUser(userId);

  const effectiveUser = fetchedUser ?? selectedUser;

  const { editState, handleEdit, handleChange, handleSave, handleCancel } = useAdminUserEdit(
    userId,
    effectiveUser,
    refetch,
  );

  const { data: adminClaimsData, loading: adminClaimsLoading } = useAdminClaims(
    {
      limit: 50,
      sortOrder: 'desc',
      user: effectiveUser?.id,
    },
    { enabled: activeTab === 'claims' && !!effectiveUser?.id },
  );

  if (!user) {
    return null;
  }

  if (userLoading && !fetchedUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} onSignOut={signOut} />
        <PageContainer maxWidth="7xl">
          <LoadingSpinner />
        </PageContainer>
      </div>
    );
  }

  const tabs: { id: AdminUserDetailsTab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'claims', label: 'Claim History' },
    { id: 'balance', label: 'Balance Management' },
  ];

  const userData = effectiveUser
    ? {
        name: effectiveUser.name,
        email: effectiveUser.email,
        picture: effectiveUser.picture,
        status: effectiveUser.status,
        role: effectiveUser.role,
        employmentType: effectiveUser.employmentType,
        jobTitle: effectiveUser.jobTitle,
        slack: effectiveUser.slack,
        github: effectiveUser.github,
        country: effectiveUser.country,
        createdDate: effectiveUser.createdDate,
        lastClaimDate: effectiveUser.lastClaimDate,
      }
    : {
        name: '—',
        email: '—',
        picture: undefined,
        status: 'inactive' as const,
        role: undefined,
        employmentType: undefined,
        jobTitle: '',
        slack: '',
        github: '',
        country: '',
        createdDate: '',
        lastClaimDate: null as string | null,
      };

  const userClaims: IClaim[] = (adminClaimsData?.items || []).map((claim: IAdminClaim) => ({
    id: claim.id,
    amount: claim.amount,
    category: claim.category,
    categoryName: claim.categoryName,
    details: claim.details,
    receipt: claim.receipt,
    status: claim.status,
    flag: claim.flag,
    aiNotes: claim.aiNotes,
    createdAt: claim.createdAt,
  }));

  const renderContent = () => {
    if (activeTab === 'details') {
      if (editState.saving) {
        return (
          <section className="rounded-lg bg-white p-6 shadow-md">
            <LoadingSpinner />
          </section>
        );
      }

      return (
        <section className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-6 flex flex-col gap-6 border-b border-gray-100 pb-6 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-pink-100">
                {userData.picture ? (
                  <img
                    src={userData.picture}
                    alt={userData.name}
                    className="size-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-red-600">
                    {userData.name !== '—' ? getInitials(userData.name) : '?'}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">{userData.name}</h2>
                  {userData.status && (
                    <Badge variant="status" square>
                      {userData.status}
                    </Badge>
                  )}
                  {userData.role && (
                    <Badge variant="role" square>
                      {userData.role}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-base text-gray-600">{userData.jobTitle || '—'}</p>
                <p className="mt-1 text-sm text-gray-500">{userData.email}</p>
              </div>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-6 text-sm md:grid-cols-4">
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">User ID</dt>
              <dd className="font-medium text-gray-900">{userData.email || '—'}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">
                Employment Type
              </dt>
              <dd className="font-medium text-gray-900">
                {userData.employmentType ? capitalizeFirstLetter(userData.employmentType) : '—'}
              </dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">Job Title</dt>
              <dd className="font-medium text-gray-900">{userData.jobTitle || '—'}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">Country</dt>
              <dd className="font-medium text-gray-900">{userData.country || '—'}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">Slack</dt>
              <dd className="font-medium text-gray-900">{userData.slack || '—'}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">GitHub</dt>
              <dd className="font-medium text-gray-900">{userData.github || '—'}</dd>
            </div>
            {deelContractIdEnabled && (
              <div>
                <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">
                  Deel Contract ID
                </dt>
                <dd className="font-medium text-gray-900">
                  {editState.editingField === 'deelContractId' ? (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={editState.values.deelContractId ?? ''}
                        onChange={(e) => handleChange('deelContractId', e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      {editState.error && (
                        <span className="text-xs text-red-600">{editState.error}</span>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSave}
                          className="rounded bg-red-600 px-2 py-1 text-xs text-white"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{effectiveUser?.deelContractId || '—'}</span>
                      {userId && (
                        <button
                          type="button"
                          onClick={() => handleEdit('deelContractId')}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label="Edit Deel contract ID"
                        >
                          <Pencil className="size-4" />
                        </button>
                      )}
                    </div>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">Created Date</dt>
              <dd className="font-medium text-gray-900">
                {userData.createdDate ? formatDate(userData.createdDate) : '—'}
              </dd>
            </div>
            <div>
              <dt className="mb-1 text-xs tracking-wide text-gray-500 uppercase">
                Last Claim Date
              </dt>
              <dd className="font-medium text-gray-900">
                {userData.lastClaimDate ? formatDate(userData.lastClaimDate) : '—'}
              </dd>
            </div>
          </dl>
        </section>
      );
    }

    if (activeTab === 'claims') {
      return (
        <section className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">All Claims</h2>
            {userClaims.length > 0 && (
              <span className="text-sm text-gray-500">Total: {userClaims.length}</span>
            )}
          </div>

          {adminClaimsLoading && userClaims.length === 0 ? (
            <p className="text-sm text-gray-500">Loading claims...</p>
          ) : userClaims.length === 0 ? (
            <p className="text-sm text-gray-500">No claims found for this user.</p>
          ) : (
            <div className="flex h-[calc(100vh-24.5rem)] flex-col">
              <ClaimsList
                claims={userClaims}
                isMobile={false}
                hasAnimated
                isVisible
                showIcon={false}
                onClaimClick={setSelectedClaim}
              />
            </div>
          )}
        </section>
      );
    }

    return (
      <section className="rounded-lg bg-white p-6 shadow-md">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Balance Management</h2>
        <p className="text-sm text-gray-600">
          Balance management tooling will be added in a future ticket.
        </p>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onSignOut={signOut} />
      <PageContainer maxWidth="7xl">
        <header className="mb-8">
          <div className="relative mb-8 flex items-center">
            <button
              onClick={() => navigate('/admin/users')}
              className="absolute left-0 flex items-center text-gray-600 transition-colors hover:text-gray-900"
              aria-label="Go back to user management"
            >
              <svg
                className="size-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="ml-8 text-3xl font-bold text-gray-900">{userData.name}</h1>
          </div>
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`border-b-2 px-1 py-4 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </header>

        <div className="mt-6">{renderContent()}</div>

        {selectedClaim && (
          <ClaimDetailsModal claim={selectedClaim} onClose={() => setSelectedClaim(null)} />
        )}
      </PageContainer>
    </div>
  );
}

export { AdminUserDetails };
